import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, Lock, Eye, EyeOff, User } from "lucide-react";
import { z } from "zod";

const registerSchema = z.object({
  firstName: z.string().min(2, "Minimaal 2 karakters"),
  lastName: z.string().min(2, "Minimaal 2 karakters"),
  email: z.string().email("Voer een geldig emailadres in"),
  password: z.string().min(8, "Minimaal 8 karakters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Wachtwoorden komen niet overeen",
  path: ["confirmPassword"],
});

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
  defaultEmail?: string;
  defaultFirstName?: string;
  defaultLastName?: string;
}

const RegisterForm = ({ 
  onSuccess, 
  onSwitchToLogin, 
  defaultEmail = "",
  defaultFirstName = "",
  defaultLastName = ""
}: RegisterFormProps) => {
  const { signUp, updateProfile } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: defaultFirstName,
    lastName: defaultLastName,
    email: defaultEmail,
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = registerSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    const { error: signUpError } = await signUp(formData.email, formData.password);

    if (signUpError) {
      toast({
        title: "Registratie mislukt",
        description: signUpError.message,
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Wait a moment for the session to be established
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Update profile with name
    await updateProfile({
      first_name: formData.firstName,
      last_name: formData.lastName,
    });

    toast({
      title: "Account aangemaakt!",
      description: "Welkom bij Alienjuice.",
    });

    setIsLoading(false);
    onSuccess?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Voornaam</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="firstName"
              name="firstName"
              placeholder="Jan"
              value={formData.firstName}
              onChange={handleChange}
              className={`pl-10 ${errors.firstName ? "border-destructive" : ""}`}
            />
          </div>
          {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Achternaam</Label>
          <Input
            id="lastName"
            name="lastName"
            placeholder="Janssen"
            value={formData.lastName}
            onChange={handleChange}
            className={errors.lastName ? "border-destructive" : ""}
          />
          {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="je@email.nl"
            value={formData.email}
            onChange={handleChange}
            className={`pl-10 ${errors.email ? "border-destructive" : ""}`}
            autoComplete="email"
          />
        </div>
        {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Wachtwoord</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Minimaal 8 karakters"
            value={formData.password}
            onChange={handleChange}
            className={`pl-10 pr-10 ${errors.password ? "border-destructive" : ""}`}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Bevestig wachtwoord</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type={showPassword ? "text" : "password"}
            placeholder="Herhaal wachtwoord"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`pl-10 ${errors.confirmPassword ? "border-destructive" : ""}`}
            autoComplete="new-password"
          />
        </div>
        {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
      </div>

      <Button type="submit" variant="glow" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Bezig met registreren...
          </>
        ) : (
          "Account aanmaken"
        )}
      </Button>

      <p className="text-sm text-center text-muted-foreground">
        Al een account?{" "}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary hover:underline font-medium"
        >
          Log hier in
        </button>
      </p>
    </form>
  );
};

export default RegisterForm;
